/**
 * Reconnecting WebSocket client for the dashboard event stream.
 *
 * Implements the subscription protocol documented in
 * ``PodPlatform/docs/DASHBOARD_EVENT_SEQUENCES.md`` and the backend
 * handler in ``pod_platform/api/app.py:dashboard_stream``:
 *
 *   client → server { action: "subscribe", subjects, types, since_id,
 *                     include_scene_hint, include_narrative }
 *   server → client { type: "subscribed" | "event" | "heartbeat" |
 *                     "ack" | "error" | "resync_required" | "pong",
 *                     payload? }
 *
 * Reconnect logic uses exponential backoff capped at 30s and persists
 * the last-seen Redis Stream id in ``localStorage`` so a page reload
 * does not lose recent events.
 */

import type { AnyEnvelope } from '@/events';

export interface SubscribeOptions {
  subjects: string[];
  types?: string[];
  sinceId?: string;
  includeSceneHint?: boolean;
  includeNarrative?: boolean;
}

export type ConnectionState =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'subscribed'
  | 'reconnecting'
  | 'closed';

export interface DashboardStreamHandlers {
  onEvent: (envelope: AnyEnvelope) => void;
  onState?: (state: ConnectionState) => void;
  onError?: (err: unknown) => void;
  /**
   * Optional REST-based gap-fill, invoked when the server emits
   * ``resync_required`` (meaning our persisted cursor has been
   * trimmed from the Redis Stream).  The handler should fetch the
   * missing window via ``/events/replay`` and feed each envelope back
   * through ``onEvent``.  After it resolves, the stream re-subscribes
   * from ``0-0`` and resumes tailing live events.
   *
   * If left undefined, the stream falls back to the original
   * behaviour (skip the gap, resume live-only).
   */
  onResync?: (sinceId: string) => Promise<void>;
}

const RECONNECT_MIN_MS = 500;
const RECONNECT_MAX_MS = 30_000;
const CURSOR_STORAGE_KEY = 'hedgefundweb.lastSinceId';

function loadCursor(): string {
  try {
    return window.localStorage.getItem(CURSOR_STORAGE_KEY) ?? '0-0';
  } catch {
    return '0-0';
  }
}

function saveCursor(id: string): void {
  try {
    window.localStorage.setItem(CURSOR_STORAGE_KEY, id);
  } catch {
    /* localStorage disabled -- best effort */
  }
}

/**
 * Resolve the WebSocket URL.  We deliberately reuse the page origin so
 * production traffic flows through the same nginx as REST and stays
 * same-origin under TLS (``wss://quantfund.ru/api/v1/stream``).  Dev
 * runs through Vite's proxy (`server.proxy.ws = true` in vite.config).
 *
 * Browsers' ``WebSocket`` API does not let JS set request headers
 * during the handshake, so the API key has to travel as a query
 * parameter -- the backend's ``_validate_ws_token`` already accepts
 * ``?token=<key>``.  We strip ``X-API-Key``-style headers from this
 * path on purpose: there's no place to put them.
 */
function streamUrl(): string {
  const override = import.meta.env.VITE_WS_URL;
  const base = override
    ? override
    : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/v1/stream`;
  const apiKey = import.meta.env.VITE_API_KEY;
  if (!apiKey) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}token=${encodeURIComponent(apiKey)}`;
}

export class DashboardStream {
  private ws: WebSocket | null = null;
  private retries = 0;
  private state: ConnectionState = 'idle';
  private pendingOptions: SubscribeOptions | null = null;
  private deliberateClose = false;

  constructor(private readonly handlers: DashboardStreamHandlers) {}

  /**
   * Connect (or reconnect) and immediately subscribe.  Safe to call
   * multiple times; calls after the first replace the active filter.
   */
  subscribe(options: SubscribeOptions): void {
    this.pendingOptions = options;
    this.deliberateClose = false;
    if (this.state === 'open' || this.state === 'subscribed') {
      this.sendSubscribe();
      return;
    }
    this.open();
  }

  close(): void {
    this.deliberateClose = true;
    this.transition('closed');
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  // -------- internals --------

  private open(): void {
    this.transition('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(streamUrl());
    } catch (err) {
      this.handlers.onError?.(err);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.retries = 0;
      this.transition('open');
      if (this.pendingOptions) this.sendSubscribe();
    });

    ws.addEventListener('message', (ev) => {
      let msg: unknown;
      try {
        msg = JSON.parse(ev.data as string);
      } catch (err) {
        this.handlers.onError?.(err);
        return;
      }
      this.handleMessage(msg);
    });

    ws.addEventListener('close', () => {
      this.ws = null;
      if (this.deliberateClose) return;
      this.scheduleReconnect();
    });

    ws.addEventListener('error', (err) => {
      this.handlers.onError?.(err);
      // ``close`` will fire after ``error`` -- reconnect happens there.
    });
  }

  private handleMessage(msg: unknown): void {
    if (typeof msg !== 'object' || msg === null) return;
    const m = msg as { type?: string; payload?: unknown };
    switch (m.type) {
      case 'subscribed':
        this.transition('subscribed');
        return;
      case 'event': {
        const env = m.payload as AnyEnvelope | undefined;
        if (!env || typeof env.id !== 'string') return;
        saveCursor(env.id);
        try {
          this.handlers.onEvent(env);
        } catch (err) {
          this.handlers.onError?.(err);
        }
        return;
      }
      case 'heartbeat':
      case 'ack':
      case 'pong':
        return;
      case 'resync_required': {
        // Cursor too old -- the events between ``loadCursor()`` and
        // "now" have been trimmed from the live stream.  Best-effort
        // fill via the REST replay endpoint, then resume tailing live
        // from a fresh cursor.  If no onResync handler is wired we
        // fall back to the original "skip the gap" behaviour so the
        // dashboard at least keeps moving.
        const sinceId = loadCursor();
        saveCursor('0-0');
        const finish = () => {
          if (this.pendingOptions) this.sendSubscribe();
        };
        if (this.handlers.onResync) {
          this.handlers.onResync(sinceId).then(finish, (err: unknown) => {
            this.handlers.onError?.(err);
            finish();
          });
        } else {
          finish();
        }
        return;
      }
      case 'error':
        this.handlers.onError?.(m.payload ?? new Error('ws server error'));
        return;
      default:
        return;
    }
  }

  private sendSubscribe(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (!this.pendingOptions) return;
    const sinceId = this.pendingOptions.sinceId ?? loadCursor();
    const payload = {
      action: 'subscribe',
      subjects: this.pendingOptions.subjects,
      types: this.pendingOptions.types ?? ['*'],
      since_id: sinceId,
      include_scene_hint: this.pendingOptions.includeSceneHint ?? true,
      include_narrative: this.pendingOptions.includeNarrative ?? false,
    };
    try {
      this.ws.send(JSON.stringify(payload));
    } catch (err) {
      this.handlers.onError?.(err);
    }
  }

  private scheduleReconnect(): void {
    this.transition('reconnecting');
    const delay = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_MIN_MS * Math.pow(2, this.retries),
    );
    this.retries += 1;
    window.setTimeout(() => {
      if (!this.deliberateClose) this.open();
    }, delay);
  }

  private transition(state: ConnectionState): void {
    if (state === this.state) return;
    this.state = state;
    this.handlers.onState?.(state);
  }
}
